import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FormikErrors, FormikTouched } from "formik";
import { IbanElement, useStripe, useElements } from "@stripe/react-stripe-js";

import { SelectCountry } from "./SelectCountry";
import { useGetAllCountriesQuery } from "@/app/app.apiCountry";

interface FormBankPaymentProps {
  handleChange: (e: React.ChangeEvent<any>) => void;
  values: {
    name: string;
    email: string;
    address: string;
    country: string;
    [key: string]: any;
  };
  errors: FormikErrors<{
    name: string;
    email: string;
    address: string;
    country: string;
    [key: string]: any;
  }>;
  touched: FormikTouched<{
    name: string;
    email: string;
    address: string;
    country: string;
    [key: string]: any;
  }>;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

export const FormBankPayment: React.FC<FormBankPaymentProps> = ({
  handleChange,
  values,
  errors,
  touched,
  setFieldValue,
}) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [ibanError, setIbanError] = useState("");

  const [optionsCountryApi, setOptionsCountryApi] = useState([]);

  //TRAER PAISES PARA SELECT COUNTRIES
  const {
    data: countries,
    isLoading: isLoadingCountries,
    isError: isErrorCountries,
  } = useGetAllCountriesQuery({});

  useEffect(() => {
    // Verifica que no esté cargando y que no haya errores
    if (!isLoadingCountries && !isErrorCountries && countries?.length > 0) {
      // Mapea y transforma los datos de los países
      console.debug(countries);
      const arrayCountries = countries.map((country) => ({
        id: country.name.common,
        name: country.name.common,
        code: country.cca2,
      }));

      // Actualiza el estado con los países formateados
      setOptionsCountryApi(arrayCountries);
    }

    // Manejamos los errores
    if (isErrorCountries) {
      console.log("ERROR EN LOS COUNTRIES");
    }
  }, [countries, isLoadingCountries, isErrorCountries]);

  const handleIbanChange = (event) => {
    if (event.error) {
      setIbanError(event.error.message);
    } else {
      setIbanError("");
    }
    setFieldValue("iban", event);
  };

  const ibanElementOptions = {
    supportedCountries: ["SEPA"],
    style: {
      base: {
        color: "#32325d",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    },
  };

  return (
    <div className="space-y-4">
      <div className="col-span-full">
        <p className="text-sm text-gray-600 text-justify font-light">
          {t("direct_debit.views.terms_2")}
        </p>
      </div>

      <div>
        <label
          htmlFor="iban"
          className="block text-sm font-medium text-gray-700"
        >
          IBAN
        </label>
        <div className="mt-1">
          <IbanElement
            id="iban"
            onChange={handleIbanChange}
            options={ibanElementOptions}
            className="block w-full rounded-md border-gray-200 focus:border-primary-500 focus:ring-primary-500 sm:text-sm border-2 py-2 px-1"
          />
        </div>
        {ibanError && <div className="text-red-600 text-sm">{ibanError}</div>}
      </div>

      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium text-gray-700"
        >
          {t("direct_debit.views.modal_name")}
        </label>
        <input
          id="name"
          name="name"
          type="text"
          onChange={handleChange}
          value={values.name}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
        />
        {errors.name && touched.name && (
          <div className="text-red-600 text-sm">{errors.name}</div>
        )}
      </div>

      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700"
        >
          {t("direct_debit.views.modal_email")}
        </label>
        <input
          id="email"
          name="email"
          type="email"
          onChange={handleChange}
          value={values.email}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
        />
        {errors.email && touched.email && (
          <div className="text-red-600 text-sm">{errors.email}</div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="address"
            className="block text-sm font-medium text-gray-700"
          >
            {t("direct_debit.views.modal_address")}
          </label>
          <input
            id="address"
            name="address"
            type="text"
            onChange={handleChange}
            value={values.address}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
          {errors.address && touched.address && (
            <div className="text-red-600 text-sm">{errors.address}</div>
          )}
        </div>

        <div>
          <SelectCountry
            span={t("general.mandatory")}
            selected={optionsCountryApi.find(
              (option) => option.id === values.country
            )}
            setSelected={(value) => {
              setFieldValue("country", value.id);
            }}
            options={optionsCountryApi}
            tittle={t("branch.views.country")}
          />
        </div>
      </div>
    </div>
  );
};
